/* CYR Final Week browser controller. Requires CYRFinalWeekEngine and CYRFinalWeekManifest. */
(function (root, factory) {
  var controller = factory(root);
  if (typeof module === "object" && module.exports) module.exports = controller;
  if (root) root.CYRFinalWeekController = controller;
})(typeof globalThis !== "undefined" ? globalThis : this, function (root) {
  "use strict";

  var BYPASS_PARAMETER = "isGiveawayLive";
  var QA_BYPASS_PARAMETER = "cyrQa";
  var STORAGE_PENDING = "cyr_final_week_boot_pending";
  var STORAGE_DISABLE_ONCE = "cyr_final_week_disable_once";
  var BANNER_COUNTDOWN_STORAGE_PREFIX = "cyr_final_week_banner_countdown";
  var BANNER_COUNTDOWN_DURATION_MS = (18 * 60 + 36) * 1000;
  var OVERLAY_ID = "cyr-final-week-overlay";
  var BANNER_ID = "cyr-giveaway-banner";
  var CTA_SELECTOR = [
    "#entry-pack-button",
    "#entry-pack-button-upsell",
    ".launch-phoenix-checkout",
    ".upsell-trigger"
  ].join(",");
  var context = null;
  var watchdog = null;
  var timer = null;
  var originalTriggerUpsell = null;
  var lastState = null;
  var bannerTracked = false;
  var bannerFallbackEndsAt = null;

  function safelyReadStorage(key) {
    try {
      return root.sessionStorage.getItem(key);
    } catch (error) {
      return null;
    }
  }

  function safelyWriteStorage(key, value) {
    try {
      root.sessionStorage.setItem(key, value);
      return true;
    } catch (error) {
      return false;
    }
  }

  function safelyRemoveStorage(key) {
    try {
      root.sessionStorage.removeItem(key);
    } catch (error) {
      // Nothing else is required when storage is unavailable.
    }
  }

  function safelyReadPersistentStorage(key) {
    try {
      var value = root.localStorage.getItem(key);
      if (value !== null) return value;
    } catch (error) {
      // Fall back to session storage when persistent storage is unavailable.
    }
    return safelyReadStorage(key);
  }

  function safelyWritePersistentStorage(key, value) {
    try {
      root.localStorage.setItem(key, value);
      return true;
    } catch (error) {
      return safelyWriteStorage(key, value);
    }
  }

  function qaBypassRequested() {
    var value = new URL(root.location.href).searchParams.get(QA_BYPASS_PARAMETER);
    return typeof value === "string" && value.toLowerCase() === "true";
  }

  function removeInternalBypass() {
    var url = new URL(root.location.href);
    if (!url.searchParams.has(BYPASS_PARAMETER)) return;
    url.searchParams.delete(BYPASS_PARAMETER);
    root.history.replaceState(root.history.state, "", url.pathname + url.search + url.hash);
  }

  function addInternalBypass() {
    var url = new URL(root.location.href);
    url.searchParams.set(BYPASS_PARAMETER, "true");
    root.history.replaceState(root.history.state, "", url.pathname + url.search + url.hash);
  }

  function scrubStoredBypass() {
    try {
      var serialized = root.sessionStorage.getItem("query_params");
      if (!serialized) return;
      var stored = JSON.parse(serialized);
      if (!stored || typeof stored !== "object") return;
      delete stored[BYPASS_PARAMETER];
      delete stored[QA_BYPASS_PARAMETER];
      if (Object.keys(stored).length) root.sessionStorage.setItem("query_params", JSON.stringify(stored));
      else root.sessionStorage.removeItem("query_params");
    } catch (error) {
      // A malformed pre-existing attribution value must not prevent the controller from loading.
    }
  }

  function getVisitorTimeZone(runtime) {
    if (runtime.visitorTimeZone) return runtime.visitorTimeZone;
    var resolved = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (!resolved) throw new Error("The browser did not provide a visitor time zone");
    return resolved;
  }

  function createContext() {
    var engine = root.CYRFinalWeekEngine;
    var manifestTemplate = root.CYRFinalWeekManifest;
    var runtime = root.CYRFinalWeekRuntime || {};
    if (!engine || !manifestTemplate || !runtime.enabled || !manifestTemplate.enabled) return null;

    var route = root.location.pathname.replace(/\/$/, "");
    // Do not validate dates or touch the page unless this exact path is in the source manifest.
    // The Liquid prefix check is only a loading optimization; this remains the authority.
    if (!engine.findOffer(manifestTemplate, route)) return null;

    var manifest = engine.resolveCampaignManifest(manifestTemplate, {
      giveawayStartDate: runtime.giveawayStartDate,
      giveawayEndDate: runtime.giveawayEndDate
    });

    engine.validateManifest(manifest);
    var offer = engine.findOffer(manifest, route);
    if (!offer) return null;

    var visitorTimeZone = getVisitorTimeZone(runtime);
    // Force an early timezone validation before the legacy PageFly gate is bypassed.
    engine.getOfferWindow(offer, manifest, visitorTimeZone);
    return {
      engine: engine,
      manifest: manifest,
      runtime: runtime,
      offer: offer,
      visitorTimeZone: visitorTimeZone,
      qaBypass: runtime.qaBypassAllowed === true && qaBypassRequested(),
      evaluation: null,
      target: null
    };
  }

  function failSafe(error) {
    if (root.console && typeof root.console.error === "function") {
      root.console.error("Final Week controller failed; restoring the legacy page gate.", error);
    }
    if (watchdog) root.clearTimeout(watchdog);
    if (timer) root.clearInterval(timer);
    removeInternalBypass();
    scrubStoredBypass();
    clearOverlay();

    if (context) {
      var reloadGuardStored = safelyWriteStorage(STORAGE_DISABLE_ONCE, context.offer.path);
      safelyRemoveStorage(STORAGE_PENDING);
      if (reloadGuardStored) {
        root.location.reload();
      } else {
        // If storage is blocked, reproduce the legacy fail-closed screen without risking a reload loop.
        var pageFlyRoot = root.document.getElementById("__pf");
        var mainContent = root.document.getElementById("MainContent");
        if (pageFlyRoot) pageFlyRoot.style.display = "none";
        if (mainContent) mainContent.innerHTML = "<h1 class='ended_promotion'>THIS PROMOTION HAS ENDED!</h1>";
      }
    }
  }

  function formatEntries(value) {
    return new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(value);
  }

  function formatPrice(value) {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(value);
  }

  function formatCountdown(milliseconds) {
    var totalSeconds = Math.max(0, Math.ceil(milliseconds / 1000));
    var hours = Math.floor(totalSeconds / 3600);
    var minutes = Math.floor((totalSeconds % 3600) / 60);
    var seconds = totalSeconds % 60;
    return String(hours).padStart(2, "0") + ":" + String(minutes).padStart(2, "0") + ":" + String(seconds).padStart(2, "0");
  }

  function bannerCountdownStorageKey() {
    var parts = [
      BANNER_COUNTDOWN_STORAGE_PREFIX,
      context.manifest.brandKey,
      context.manifest.giveawayEndLocalDate,
      context.offer.id
    ];
    if (context.qaBypass) parts.push("qa");
    return parts.join(":");
  }

  function getBannerCountdown(now) {
    var key = bannerCountdownStorageKey();
    var storedEndsAt = Number(safelyReadPersistentStorage(key));
    var endsAt = Number.isFinite(storedEndsAt) && storedEndsAt > 0 ? storedEndsAt : null;

    if (!endsAt) {
      endsAt = now + BANNER_COUNTDOWN_DURATION_MS;
      if (!safelyWritePersistentStorage(key, String(endsAt))) {
        if (!bannerFallbackEndsAt) bannerFallbackEndsAt = endsAt;
        endsAt = bannerFallbackEndsAt;
      }
    }

    return {
      endsAt: endsAt,
      remainingMilliseconds: Math.max(
        0,
        Math.min(endsAt - now, context.evaluation.remainingMilliseconds)
      ),
      completed: endsAt <= now
    };
  }

  function analytics(eventName, details) {
    // Public QA views must not contaminate storefront analytics.
    if (context && context.qaBypass) return;
    try {
      var payload = Object.assign({
        event: eventName,
        source_offer: context.offer.id,
        source_path: context.offer.path,
        lane: context.offer.lane,
        visitor_time_zone: context.visitorTimeZone
      }, details || {});
      if (Array.isArray(root.dataLayer)) root.dataLayer.push(payload);
      if (typeof root.CustomEvent === "function") {
        root.document.dispatchEvent(new root.CustomEvent("cyr:final-week", { detail: payload }));
      }
    } catch (error) {
      // Analytics must never affect offer availability or the purchase guard.
    }
  }

  function isBlocked() {
    return context && context.evaluation && !context.evaluation.purchasable;
  }

  function setCtaState(disabled) {
    root.document.querySelectorAll(CTA_SELECTOR).forEach(function (element) {
      if (disabled) {
        element.classList.add("cyr-final-week-cta-disabled");
        element.setAttribute("aria-disabled", "true");
        if (!element.hasAttribute("data-cyr-original-tabindex")) {
          element.setAttribute("data-cyr-original-tabindex", element.getAttribute("tabindex") || "");
        }
        element.setAttribute("tabindex", "-1");
      } else {
        element.classList.remove("cyr-final-week-cta-disabled");
        element.removeAttribute("aria-disabled");
        if (element.hasAttribute("data-cyr-original-tabindex")) {
          var originalTabIndex = element.getAttribute("data-cyr-original-tabindex");
          if (originalTabIndex) element.setAttribute("tabindex", originalTabIndex);
          else element.removeAttribute("tabindex");
          element.removeAttribute("data-cyr-original-tabindex");
        }
      }
    });
  }

  function installPurchaseGuards() {
    root.document.addEventListener("click", function (event) {
      if (!isBlocked() || !event.target.closest || !event.target.closest(CTA_SELECTOR)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      showOverlay();
    }, true);

    if (typeof root.TriggerUpsell === "function" && !originalTriggerUpsell) {
      originalTriggerUpsell = root.TriggerUpsell;
      root.TriggerUpsell = function () {
        if (isBlocked()) {
          showOverlay();
          return false;
        }
        return originalTriggerUpsell.apply(this, arguments);
      };
    }
  }

  function targetDescription(target) {
    if (!target.offer) return "our current giveaway page";
    return formatEntries(target.offer.entries) + " bonus entries for " + formatPrice(target.offer.price);
  }

  function overlayContent() {
    var state = context.evaluation.state;
    var target = context.target;
    var sourceDescription = formatEntries(context.offer.entries) + " bonus entries for " + formatPrice(context.offer.price);

    if (state === context.engine.STATES.PRELAUNCH) {
      return {
        eyebrow: "Final Week offer",
        title: "This offer is not available yet",
        message: "The " + sourceDescription + " offer has not started. You can view " + targetDescription(target) + " instead.",
        button: target.offer ? "View the current offer" : "Continue to the giveaway"
      };
    }

    if (state === context.engine.STATES.CAMPAIGN_ENDED) {
      return {
        eyebrow: "Giveaway update",
        title: "This promotion has ended",
        message: "Final Week is now closed. Continue to our homepage for the latest information.",
        button: "Go to homepage"
      };
    }

    return {
      eyebrow: "Offer update",
      title: "This offer has ended",
      message: "The " + sourceDescription + " offer is over, but you can still get " + targetDescription(target) + ".",
      button: target.offer ? "View today's offer" : "Continue to the giveaway"
    };
  }

  function trapDialogFocus(dialog) {
    dialog.addEventListener("keydown", function (event) {
      if (event.key !== "Tab") return;
      var focusable = Array.from(dialog.querySelectorAll("a[href],button:not([disabled]),[tabindex]:not([tabindex='-1'])"));
      if (!focusable.length) return;
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (event.shiftKey && root.document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && root.document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
  }

  function showOverlay() {
    if (!isBlocked()) return;
    var existing = root.document.getElementById(OVERLAY_ID);
    if (existing) return;

    var content = overlayContent();
    var overlay = root.document.createElement("div");
    overlay.id = OVERLAY_ID;
    overlay.className = "cyr-final-week-overlay";
    overlay.setAttribute("role", "presentation");
    var brandColor = String(context.runtime.brandColor || "").trim();
    if (/^[0-9a-f]{3,8}$/i.test(brandColor)) brandColor = "#" + brandColor;
    if (/^#[0-9a-f]{3,8}$/i.test(brandColor)) {
      overlay.style.setProperty("--cyr-final-week-brand-color", brandColor);
    }

    var dialog = root.document.createElement("section");
    dialog.className = "cyr-final-week-dialog";
    dialog.setAttribute("role", "dialog");
    dialog.setAttribute("aria-modal", "true");
    dialog.setAttribute("aria-labelledby", "cyr-final-week-title");
    dialog.setAttribute("aria-describedby", "cyr-final-week-message");

    var eyebrow = root.document.createElement("p");
    eyebrow.className = "cyr-final-week-eyebrow";
    eyebrow.textContent = content.eyebrow;

    var title = root.document.createElement("h1");
    title.id = "cyr-final-week-title";
    title.className = "cyr-final-week-title";
    title.textContent = content.title;

    var message = root.document.createElement("p");
    message.id = "cyr-final-week-message";
    message.className = "cyr-final-week-message";
    message.textContent = content.message;

    var button = root.document.createElement("a");
    button.className = "cyr-final-week-button";
    var destinationPath = context.evaluation.state === context.engine.STATES.CAMPAIGN_ENDED
      ? "/"
      : context.target.path;
    button.href = context.engine.buildTargetUrl(destinationPath, root.location.search, context.manifest);
    button.textContent = content.button;
    button.addEventListener("click", function () {
      analytics("fw_offer_fallback_click", {
        target_offer: context.target.offer ? context.target.offer.id : null,
        target_path: destinationPath,
        target_reason: context.target.reason
      });
    });

    dialog.appendChild(eyebrow);
    dialog.appendChild(title);
    dialog.appendChild(message);
    dialog.appendChild(button);
    overlay.appendChild(dialog);
    root.document.body.appendChild(overlay);
    root.document.documentElement.classList.add("cyr-final-week-locked");
    root.document.body.classList.add("cyr-final-week-locked");
    trapDialogFocus(dialog);
    button.focus();

    analytics("fw_offer_expired_overlay", {
      state: context.evaluation.state,
      target_offer: context.target.offer ? context.target.offer.id : null,
      target_path: destinationPath,
      target_reason: context.target.reason
    });
  }

  function clearOverlay() {
    var existing = root.document.getElementById(OVERLAY_ID);
    if (existing && existing.parentNode) existing.parentNode.removeChild(existing);
    root.document.documentElement.classList.remove("cyr-final-week-locked");
    if (root.document.body) root.document.body.classList.remove("cyr-final-week-locked");
  }

  function updateBanner() {
    if (!context.manifest.bannerEnabled || !context.runtime.bannerEnabled) return;
    var banner = root.document.getElementById(BANNER_ID);
    if (!banner) return;
    if (!context.evaluation.purchasable) {
      if (!banner.hidden) banner.hidden = true;
      if (banner.hasAttribute("data-cyr-final-week-active")) {
        banner.removeAttribute("data-cyr-final-week-active");
      }
      return;
    }

    if (banner.hidden) banner.hidden = false;
    if (banner.getAttribute("data-cyr-final-week-active") !== "true") {
      banner.setAttribute("data-cyr-final-week-active", "true");
    }

    var copy = banner.querySelector(".cyr-final-week-banner-copy");
    if (!copy) {
      copy = root.document.createElement("span");
      copy.className = "cyr-final-week-banner-copy";
      banner.replaceChildren(copy);
    }

    var now = Date.now();
    var offerLabel = formatEntries(context.offer.entries) + " BONUS ENTRIES FOR " + formatPrice(context.offer.price);
    var timerState = getBannerCountdown(now);
    var offerCopy = root.document.createElement("span");
    offerCopy.className = "cyr-final-week-banner-offer";
    offerCopy.textContent = offerLabel;

    copy.replaceChildren(offerCopy);
    if (!timerState.completed && timerState.remainingMilliseconds > 0) {
      var separator = root.document.createElement("span");
      separator.className = "cyr-final-week-banner-separator";
      separator.setAttribute("aria-hidden", "true");
      separator.textContent = " \u2022 ";

      var urgency = root.document.createElement("span");
      urgency.className = "cyr-final-week-banner-urgency";
      urgency.appendChild(root.document.createTextNode("ENDS IN "));

      var countdown = root.document.createElement("span");
      countdown.className = "cyr-final-week-countdown";
      countdown.setAttribute("aria-hidden", "true");
      countdown.textContent = formatCountdown(timerState.remainingMilliseconds);
      urgency.appendChild(countdown);
      copy.appendChild(separator);
      copy.appendChild(urgency);
      banner.setAttribute(
        "aria-label",
        offerLabel + ". Ends in " + formatCountdown(timerState.remainingMilliseconds) + "."
      );
    } else {
      banner.setAttribute("aria-label", offerLabel + ".");
    }
    if (!bannerTracked) {
      analytics("fw_offer_banner_impression", { state: context.evaluation.state });
      bannerTracked = true;
    }
  }

  function evaluateAndRender() {
    var now = Date.now();
    var previousState = context.evaluation ? context.evaluation.state : null;
    context.evaluation = context.engine.evaluateOffer(
      context.offer,
      context.manifest,
      now,
      context.visitorTimeZone
    );

    if (context.qaBypass) {
      // Render this exact manifest offer as live without changing its schedule.
      // The tagged-account QA parameter is deliberately honored only after
      // exact-route validation in createContext().
      context.evaluation = Object.assign({}, context.evaluation, {
        state: context.engine.STATES.ACTIVE,
        purchasable: true,
        expiresAt: now + BANNER_COUNTDOWN_DURATION_MS,
        remainingMilliseconds: BANNER_COUNTDOWN_DURATION_MS,
        qaBypass: true
      });
      context.target = {
        type: "offer",
        reason: "qa_bypass",
        offer: context.offer,
        path: context.offer.path
      };
    } else {
      context.target = context.engine.resolveTarget(
        context.manifest,
        context.offer,
        now,
        context.visitorTimeZone
      );
    }

    setCtaState(!context.evaluation.purchasable);
    updateBanner();
    if (context.evaluation.purchasable) clearOverlay();
    else showOverlay();

    if (previousState !== context.evaluation.state && lastState !== context.evaluation.state) {
      analytics(context.evaluation.state === context.engine.STATES.GRACE ? "fw_offer_grace_view" : "fw_offer_state", {
        state: context.evaluation.state,
        expires_at: new Date(context.evaluation.expiresAt).toISOString()
      });
      lastState = context.evaluation.state;
    }
  }

  function initialize() {
    try {
      evaluateAndRender();
      installPurchaseGuards();
      removeInternalBypass();
      scrubStoredBypass();
      safelyRemoveStorage(STORAGE_PENDING);
      safelyRemoveStorage(STORAGE_DISABLE_ONCE);
      if (watchdog) root.clearTimeout(watchdog);
      timer = root.setInterval(function () {
        try {
          evaluateAndRender();
        } catch (error) {
          failSafe(error);
        }
      }, 1000);
      analytics("fw_offer_controller_ready", { state: context.evaluation.state });
    } catch (error) {
      failSafe(error);
    }
  }

  function preflight() {
    try {
      context = createContext();
      if (!context) return { active: false };

      if (safelyReadStorage(STORAGE_PENDING) === context.offer.path) {
        // A prior page load was interrupted after adding the temporary bypass but before the guard was ready.
        removeInternalBypass();
        scrubStoredBypass();
        safelyRemoveStorage(STORAGE_PENDING);
        var interruptedReloadGuardStored = safelyWriteStorage(STORAGE_DISABLE_ONCE, context.offer.path);
        if (interruptedReloadGuardStored) root.location.reload();
        return { active: false, failSafe: true, reason: "interrupted_boot" };
      }

      if (safelyReadStorage(STORAGE_DISABLE_ONCE) === context.offer.path) {
        safelyRemoveStorage(STORAGE_DISABLE_ONCE);
        removeInternalBypass();
        scrubStoredBypass();
        return { active: false, failSafe: true };
      }

      addInternalBypass();
      safelyWriteStorage(STORAGE_PENDING, context.offer.path);
      watchdog = root.setTimeout(function () {
        failSafe(new Error("Final Week controller initialization timed out"));
      }, 15000);

      root.addEventListener("pagehide", removeInternalBypass, { once: true });

      var scheduleInitialize = function () { root.setTimeout(initialize, 0); };
      if (root.document.readyState === "loading") {
        root.document.addEventListener("DOMContentLoaded", scheduleInitialize, { once: true });
      } else {
        scheduleInitialize();
      }

      return {
        active: true,
        path: context.offer.path,
        visitorTimeZone: context.visitorTimeZone,
        qaBypass: context.qaBypass
      };
    } catch (error) {
      context = null;
      removeInternalBypass();
      scrubStoredBypass();
      if (root.console && typeof root.console.error === "function") {
        root.console.error("Final Week preflight was not activated.", error);
      }
      return { active: false, error: error.message };
    }
  }

  return Object.freeze({
    preflight: preflight,
    BANNER_COUNTDOWN_DURATION_MS: BANNER_COUNTDOWN_DURATION_MS,
    formatCountdown: formatCountdown,
    formatEntries: formatEntries,
    formatPrice: formatPrice
  });
});
